import React, { useState } from "react";
import { Button, Table, Form } from "react-bootstrap";
import { FaPlus, FaSearch, FaTrash } from "react-icons/fa";
import SearchDrawer from "./SearchDrawer";

const OrderRelatedDetails = ({
  lineItems,
  setLineItems,
  stockItemList = [],
  ledgersList = [],
  showErrors = {},
}) => {
  const [drawer, setDrawer] = useState({ show: false, rowIndex: null });

  const expenseLedgers = ledgersList.filter(
    (l) =>
      l.groupLabel === "Indirect Expenses" || l.groupLabel === "Duties & Taxes"
  );

  // Add with defaults
  const addLineItem = () => {
    setLineItems([
      ...lineItems,
      {
        item: "",
        rate: 0,
        quantity: 1,
        units: "",
        amount: 0,
        isLedger: false,
        isStockItem: false,
      },
    ]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length === 1) return; // prevent removing the last one
    const updated = [...lineItems];
    updated.splice(index, 1);
    setLineItems(updated);
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = value;

    if (field === "rate" || field === "quantity") {
      updated[index].amount =
        (parseFloat(updated[index].rate) || 0) *
        (parseFloat(updated[index].quantity) || 0);
    }
    setLineItems(updated);
  };

  const openDrawer = (rowIndex) => setDrawer({ show: true, rowIndex });
  const closeDrawer = () => setDrawer({ show: false, rowIndex: null });

  const handleSelectItem = (item) => {
    const updated = [...lineItems];
    const row = updated[drawer.rowIndex];
    row.item = item;

    row.isLedger =
      item.groupLabel === "Indirect Expenses" ||
      item.groupLabel === "Duties & Taxes";
    row.isStockItem = !row.isLedger;

    if (row.isLedger) {
      row.rate = 0;
      row.quantity = 1;
    } else {
      row.rate = item.rate ?? 0;
      row.quantity = row.quantity ?? 1;
    }

    row.units = item.units ?? "";
    row.amount = row.rate * (row.quantity ?? 1);

    setLineItems(updated);
    closeDrawer();
  };

  return (
    <div className="bg-white p-3 mt-4">
      <div className="d-flex justify-content-between mb-3">
        <h6 className="fw-bold mb-0 border-bottom pb-2">Line Items</h6>
        <Button size="sm" variant="success" onClick={addLineItem}>
          <FaPlus className="me-2" /> Add
        </Button>
      </div>

      <Table bordered responsive>
        <thead className="table-success text-center">
          <tr>
            <th>
              Item <span className="text-danger">*</span>
            </th>
            <th>Rate</th>
            <th>Quantity</th>
            <th>Units</th>
            <th>Amount</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {lineItems.map((row, idx) => (
            <tr key={idx}>
              {/* Item */}
              <td>
                <div
                  className={`form-control d-flex justify-content-between align-items-center ${
                    showErrors.item && !row.item && showErrors.itemIdx === idx
                      ? "border-danger"
                      : ""
                  }`}
                  style={{ cursor: "pointer" }}
                  onClick={() => openDrawer(idx)}
                >
                  <span className={row.item ? "text-dark" : "text-muted"}>
                    {typeof row.item === "object"
                      ? row.item.label
                      : row.item || "Search Items..."}
                  </span>
                  <FaSearch className="text-muted ms-2" />
                </div>
                {showErrors.item && !row.item && showErrors.itemIdx === idx && (
                  <small className="text-danger">Item is required</small>
                )}
              </td>

              {/* Rate */}
              <td>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={row.rate ?? 0}
                  disabled={row.isLedger}
                  onChange={(e) =>
                    updateLineItem(idx, "rate", parseFloat(e.target.value) || 0)
                  }
                />
              </td>

              {/* Quantity */}
              <td>
                <Form.Control
                  type="number"
                  value={row.quantity ?? 1}
                  disabled={row.isLedger}
                  onChange={(e) =>
                    updateLineItem(
                      idx,
                      "quantity",
                      parseFloat(e.target.value) || 1
                    )
                  }
                />
              </td>

              {/* Units */}
              <td>
                <Form.Control disabled value={row.units ?? ""} />
              </td>

              {/* Amount */}
              <td>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={(row.amount ?? 0).toFixed(2)}
                  onChange={(e) =>
                    updateLineItem(
                      idx,
                      "amount",
                      parseFloat(e.target.value) || 0
                    )
                  }
                />
              </td>

              {/* Action */}
              <td className="text-center">
                <Button
                  size="sm"
                  variant="danger"
                  className={
                    lineItems.length === 1 ? "disabled opacity-50" : ""
                  }
                  onClick={() => removeLineItem(idx)}
                  disabled={lineItems.length === 1}
                >
                  <FaTrash />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <SearchDrawer
        show={drawer.show}
        onHide={closeDrawer}
        title="Select Stock Item / Ledger"
        options={[...stockItemList, ...expenseLedgers]}
        onSelect={handleSelectItem}
      />
    </div>
  );
};

export default OrderRelatedDetails;
